import * as server from '../server';
import { replaceState } from '../history';
import URLX, { equalsUrl } from '../Url';
import { autoResolveDynamicData } from '../DynamicData';
import { saveAppShellData } from '../AppShell';
import { UPDATE_APP_SHELL_DATA } from '../AppShell/action-creators';
import { batchActions } from 'redux-batched-actions';
import { getCacheItem, clearCacheItem } from './cache';
import { clearCacheAndUnregisterServiceWorker } from '../service-worker/registration';
import { scrollPosition as rootScrollPosition } from '../Root';
import * as userLog from '../user-log';
import { coalesceName } from '../Theming';
import { resolveComponentAndChildComponents, resolveThemeContext, isBundleLoadError } from '../resolve-component';
import { showSpinner, isCommunicatingWithServer, changeProgress } from '../Spinner/action-creators';
import { dispatchScopeEvent as scopeEventDispatch } from '../events';
export const PAGE_LOAD = 'PAGE_LOAD';
export const PAGE_LOAD_CACHED = 'PAGE_LOAD_CACHED';
export const PAGE_LOAD_SUCCESS = 'PAGE_LOAD_SUCCESS';
export const PAGE_LOAD_FAILURE = 'PAGE_LOAD_FAILURE';
export const PAGE_LOAD_DONE = 'PAGE_LOAD_DONE';
export const PAGE_LOAD_ABORTED = 'PAGE_LOAD_ABORTED';
export const PAGE_UNHANDLED_ERROR = 'PAGE_UNHANDLED_ERROR';
export const PAGE_UPDATE = 'PAGE_UPDATE';
export const PAGE_UPDATE_SUCCESS = 'PAGE_UPDATE_SUCCESS';
export const PAGE_UPDATE_FAILURE = 'PAGE_UPDATE_FAILURE';
let lastPageLoadPromise = Promise.resolve();
export function unhandledError(error) {
    return {
        type: PAGE_UNHANDLED_ERROR,
        error: {
            message: error,
        },
    };
}
export function pageUpdate(requestId) {
    const action = {
        type: PAGE_UPDATE,
        requestId,
    };
    return action;
}
export function pageUpdateSuccess(requestIds, page) {
    const action = {
        type: PAGE_UPDATE_SUCCESS,
        page,
        requestIds,
    };
    return action;
}
export function pageUpdateFailure(requestIds) {
    const action = {
        type: PAGE_UPDATE_FAILURE,
        requestIds,
    };
    return action;
}
export function updateCurrentPage(updater) {
    return (dispatch, getState) => {
        const page = updater(getState().currentPage);
        if (!page.componentName) {
            throw new Error('The updater did not return a component. Use eg. "oldPage => ({...oldPage, propToUpdate: newValue})" for the updater.');
        }
        const pageUpdateSuccessAction = {
            type: PAGE_UPDATE_SUCCESS,
            page,
            requestIds: null, // We set null here because we never send a PAGE_UPDATE so this shouldn't affect isLoading
        };
        scopeEventDispatch(pageUpdateSuccessAction);
        dispatch(pageUpdateSuccessAction);
    };
}
export function loadPage(args) {
    const { options = {}, stateChangeEvent, shouldUseResponse, replaceStateOnRedirect = false } = args;
    return (dispatch, getState) => {
        dispatch(changeProgress(0));
        const url = new URLX(args.url);
        const href = url.href;
        let scrollPosition = 0;
        if (stateChangeEvent && stateChangeEvent.scrollPosition) {
            scrollPosition = stateChangeEvent.scrollPosition;
        }
        const scrollPositionKey = 'scrollPosition';
        if (options.hasOwnProperty(scrollPositionKey)) {
            scrollPosition = options.scrollPosition;
        }
        const urlBeforeLoad = new URLX(getState().currentPage.url);
        const userIsGoingToNewPage = !((stateChangeEvent && stateChangeEvent.event === 'pop') ||
            // Only hash has changed
            (equalsUrl(url, urlBeforeLoad) && url.hash !== urlBeforeLoad.hash));
        if (userIsGoingToNewPage) {
            userLog.addUserLog('Going from ' + urlBeforeLoad.href + ' to ' + url.href);
        }
        else {
            userLog.addUserLog('Going back to ' + url.href + ' from ' + urlBeforeLoad.href);
        }
        let headers = {};
        if (options.linkIdentifier) {
            headers = {
                [server.LINK_IDENTIFIER_HEADER]: options.linkIdentifier,
                [server.REQUEST_ACTION_HEADER]: server.CLICK_ACTION,
            };
        }
        if (options.includeAppShellData) {
            headers[server.INCLUDE_APP_SHELL_DATA_HEADER] = 'true';
            if (options.resolveDynamicData !== false) {
                options.resolveDynamicData = true;
            }
        }
        if (options.resolveDynamicData) {
            headers[server.RESOLVE_DYNAMIC_DATA_HEADER] = 'true';
        }
        let loadPromise = null;
        let loadHasCompleted = false;
        const load = () => {
            if (loadPromise) {
                return loadPromise;
            }
            const urlWithHiddenQuery = url.hiddenHref;
            if (urlWithHiddenQuery) {
                loadPromise = server.get(urlWithHiddenQuery, headers);
                loadPromise.then(() => (loadHasCompleted = true), () => (loadHasCompleted = true));
                return loadPromise;
            }
            else {
                return Promise.reject('Invalid URL');
            }
        };
        // If the user is going to a new page, we start the network request early
        // because the cache can take ~50ms to respond and we don't want to wait
        // ~50ms to start the request.
        // If the user isn't going to a new page but going back, we don't want to
        // issue a new request because we should have that page cached. But if we don't
        // we load it after we know that the cache didn't give us anything.
        if (userIsGoingToNewPage) {
            load();
        }
        let serverRedirected = false;
        let serverRedirectedToUrl = null;
        const initialProgressAction = changeProgress(0.33);
        const loadResources = Promise.resolve(href ? getCacheItem(url) : null)
            .then(cachedItem => {
            // This check is needed because we might want to ignore a cached response
            // like if you searched for something that gave no results we don't want to use
            // that response in a preview search.
            if (cachedItem) {
                const data = shouldUseResponse && !shouldUseResponse(cachedItem) ? null : cachedItem;
                if (data) {
                    return resolveComponentsAndTheme(data, cachedItem.currentTheme || getState().currentTheme);
                }
            }
            return null;
        })
            .then((cachedItem) => {
            const isPartial = !!cachedItem && cachedItem.$cache === 'partial';
            const isFull = !!cachedItem && cachedItem.$cache === 'full';
            const isCacheAvailable = isPartial || isFull;
            // If we want to merge the state we don't want to use the cached version
            // because that has already been merged once. Merging it again would
            // cause undefined behavior.
            const allowRenderCache = !((isPartial && options.skipPartialCache) || typeof options.merge === 'function');
            const willRenderCache = isCacheAvailable && allowRenderCache;
            const willCommunicateWithServer = (isPartial && allowRenderCache) || !isFull;
            const actions = [];
            if (currentPromise === lastPageLoadPromise && willCommunicateWithServer) {
                actions.push(isCommunicatingWithServer(true));
                actions.push(initialProgressAction);
                if (options.hideSpinner !== true) {
                    actions.push(showSpinner());
                }
            }
            if (actions.length > 0) {
                actions.forEach(scopeEventDispatch);
                dispatch(batchActions(actions));
            }
            return {
                willRenderCache,
                cachedItem,
                isPartial,
            };
        });
        const currentPromise = (lastPageLoadPromise = Promise.all([loadResources, options.deferLoadPromise])
            .then(([{ cachedItem, willRenderCache, isPartial }]) => {
            const willLoadPage = userIsGoingToNewPage || !cachedItem || isPartial || options.includeAppShellData;
            const actions = [];
            // We don't want to dispatch PAGE_LOAD if we at this
            // point already have the server response. Then we want
            // to wait and dispatch the PAGE_LOAD together with
            // PAGE_LOAD_SUCCESS at once.
            let pageLoadWasDispatched = false;
            const createPageLoad = isCurrentPage => ({
                type: PAGE_LOAD,
                url,
                urlBeforeLoad,
                options,
                isCurrentPage,
            });
            const createPageCache = isCurrentPage => ({
                type: PAGE_LOAD_CACHED,
                url,
                urlBeforeLoad,
                cachedItem,
                scrollPosition,
                options,
                isPartial,
                isCurrentPage,
            });
            const pageLoadDone = {
                type: PAGE_LOAD_DONE,
                serverRedirected,
            };
            if (!loadHasCompleted) {
                actions.push(initialProgressAction);
                const isCurrentPage = currentPromise === lastPageLoadPromise;
                actions.push(createPageLoad(isCurrentPage));
                if (willRenderCache) {
                    actions.push(createPageCache(isCurrentPage));
                }
                if (!willLoadPage) {
                    actions.push(pageLoadDone);
                }
                try {
                    pageLoadWasDispatched = true;
                    actions.forEach(scopeEventDispatch);
                    dispatch(batchActions(actions));
                }
                catch (e) {
                    if (process.env.NODE_ENV !== 'production') {
                        const div = document.createElement('div');
                        div.style.position = 'fixed';
                        div.style.zIndex = '999999999';
                        div.style.border = '5px solid red';
                        div.style.background = 'white';
                        div.onclick = () => document.body.removeChild(div);
                        div.style.padding = '20px';
                        div.style.top = '10px';
                        div.style.left = '10px';
                        div.style.right = '10px';
                        div.style.bottom = '10px';
                        div.innerHTML =
                            '<h1 style="color:red">The page could not be rendered from a cached response</h1>' +
                                '<p>It seems that the current page can not be rendered with a cached response. This ' +
                                'most likely happened because the backend has sent new data to this page and the component to render it assumes' +
                                ' that it is there. If you absolutely must do this and can not check if the data is there, make sure that the next release ' +
                                'increments the major version as that will clear the users cache.<br>' +
                                'The full error has been logged to the console.' +
                                '<h4>Error:</h4>' +
                                '<pre>' +
                                e.message +
                                '</pre>';
                        document.body.appendChild(div);
                        console.error(e);
                    }
                    else if (href) {
                        // If this error occurs in production we can't really recover from it
                        // So we send it to Raygun and reload the page since that will
                        if (window.rg4js) {
                            window.rg4js('send', e);
                        }
                        return clearCacheItem(url).then(() => {
                            window.location.reload();
                            return new Promise(() => null);
                        });
                    }
                }
            }
            // We only want to load the page from the server in the case that:
            // - The user is not going back to a page shes seen before
            // - The page we're displaying is only partially loaded
            // - We're in a loading state because we're waiting for the new page
            if (willLoadPage) {
                return load()
                    .then((r) => {
                    dispatch(changeProgress(0.67));
                    // This allows the server to use redirects. When the server redirects us
                    // `fetch` won't tell us that, so the url in the address bar won't reflect
                    // what we loaded. By using a custom header we can get what `fetch` won't give us.
                    // When that happens we call `replaceState` with a flag that we shouldn't give
                    // any events for it since we don't need to load that page.
                    const actualUrlHeader = 'x-actual-url';
                    const isLastCurrentPage = currentPromise === lastPageLoadPromise;
                    const actualUrl = r.headers.has(actualUrlHeader) && r.headers.get(actualUrlHeader);
                    if (actualUrl) {
                        const redirectUrl = new URLX(decodeURIComponent(actualUrl));
                        if (redirectUrl.pathname !== url.pathname) {
                            for (const key in redirectUrl.searchParams.keys()) {
                                if (url.hiddenParams.has(key)) {
                                    redirectUrl.searchParams.delete(key);
                                }
                            }
                            serverRedirected = true;
                            serverRedirectedToUrl = redirectUrl;
                            if (isLastCurrentPage && replaceStateOnRedirect) {
                                replaceState(redirectUrl, {}, false);
                            }
                        }
                    }
                    const serverVersion = r.headers.get(server.VERSION_HEADER_NAME);
                    if (serverVersion !== CURRENT_VERSION) {
                        console.info('Reloading the page since the server has a new version');
                        // Note that something similar to this is already done in server.tsx
                        // but that only checks for a major version change. Here we check for
                        // any version change and only clear the service worker cache. We don't
                        // unregister the SW or clear other caches, we just want the new scripts/styles
                        const clearCache = clearCacheAndUnregisterServiceWorker();
                        // If the page load wasn't initiated by the user we don't want to reload
                        // the page because that can give us infinite reloads
                        if (options.skipReloadOnVersionMismatch !== true) {
                            clearCache.then(() => {
                                rootScrollPosition(0);
                                window.location.reload();
                            });
                            // Return never resolving promise to wait for the page to reload
                            return new Promise(() => null);
                        }
                        if (serverVersion && server.majorVersionDiffers(serverVersion, CURRENT_VERSION)) {
                            // If major version differs we can't be sure if we can safely use this response
                            return new Promise(() => null);
                        }
                    }
                    return r.json();
                })
                    .then((json) => {
                    if (isExternalRedirect(json)) {
                        window.location.href = json.externalTargetUrl;
                        return new Promise(() => null);
                    }
                    autoResolveDynamicData(json);
                    const shouldUse = shouldUseResponse ? shouldUseResponse(json) : true;
                    const loadActions = [];
                    const jsonWithAppShellData = [json];
                    if (pageHasAppShellData(json)) {
                        const appShellData = json.appShellData;
                        const updateAppShellData = {
                            type: UPDATE_APP_SHELL_DATA,
                            appShellData,
                        };
                        saveAppShellData(appShellData);
                        loadActions.push(updateAppShellData);
                        delete json.appShellData;
                        jsonWithAppShellData.push(appShellData);
                    }
                    if (!shouldUse) {
                        console.info('Ignoring response because initiator did not want it');
                        loadActions.push({
                            type: PAGE_LOAD_ABORTED,
                        });
                        return dispatch(batchActions(loadActions));
                    }
                    return resolveComponentsAndTheme(jsonWithAppShellData, json.currentTheme || getState().currentTheme).then(() => {
                        const isLastCurrentPage = currentPromise === lastPageLoadPromise;
                        if (!pageLoadWasDispatched) {
                            loadActions.push(createPageLoad(isLastCurrentPage));
                        }
                        const pageLoadSuccessAction = {
                            type: PAGE_LOAD_SUCCESS,
                            page: json,
                            scrollPosition,
                            options,
                            url: serverRedirectedToUrl || url,
                            urlBeforeLoad,
                            isCurrentPage: isLastCurrentPage,
                        };
                        const pageLoadDoneAction = {
                            type: PAGE_LOAD_DONE,
                            serverRedirected,
                        };
                        loadActions.push(pageLoadSuccessAction);
                        loadActions.push(pageLoadDoneAction);
                        loadActions.forEach(scopeEventDispatch);
                        dispatch(batchActions(loadActions));
                        pageLoadWasDispatched = true;
                        return json;
                    });
                });
            }
        })
            .catch(e => {
            if (process.env.NODE_ENV === 'production') {
                if (window.rg4js) {
                    window.rg4js('send', {
                        error: e,
                        customData: {
                            currentUrl: urlBeforeLoad.href,
                            loadUrl: href,
                        },
                        userLog: userLog.userLogToString(),
                        tags: ['handled'],
                    });
                }
            }
            else {
                console.error(e);
            }
            if (currentPromise !== lastPageLoadPromise) {
                console.info('Ignoring page load failure because another request is in-flight');
                return Promise.resolve(null);
            }
            const urlHasNotChangedSinceLoad = equalsUrl(urlBeforeLoad, new URLX(getState().currentPage.url));
            const stillWaitingForPage = (urlHasNotChangedSinceLoad && getState().currentPage.isLoading && !getState().currentPage.isPartial) ||
                isBundleLoadError(e);
            let retry;
            if (stillWaitingForPage && stateChangeEvent) {
                // If we're still waiting for the page and haven't been able to show a preview
                // of the new page, revert the state change and make `retry` be the opposite of
                // the revert
                retry = stateChangeEvent.revert();
            }
            else {
                // If we've managed to show something to the user on the new page but the full
                // page failed to load, we just try to load it again
                retry = () => loadPage({ url, options, stateChangeEvent })(dispatch, getState);
            }
            const pageLoadFailAction = {
                type: PAGE_LOAD_FAILURE,
                error: e,
                url,
                retry,
                loadFailure: {
                    url: href,
                    status: e.status,
                    isShowingFullCachedPage: !urlHasNotChangedSinceLoad && !getState().currentPage.isLoading && !getState().currentPage.isPartial,
                    isShowingPartialCachedPage: !urlHasNotChangedSinceLoad && getState().currentPage.isPartial,
                },
            };
            const actions = batchActions([
                pageLoadFailAction,
                {
                    type: PAGE_LOAD_DONE,
                    serverRedirected,
                },
            ]);
            const contentType = e.response ? e.response.headers.get('content-type') : null;
            const isJson = contentType && contentType.indexOf('application/json') !== -1;
            if (isJson) {
                e.response.json().then((json) => {
                    resolveComponentsAndTheme(json, json.currentTheme || getState().currentTheme).then((page) => {
                        pageLoadFailAction.loadFailure.response = page;
                        dispatch(actions);
                    });
                });
            }
            else {
                dispatch(actions);
            }
            // If we're still on the same page as when we started loading, reject
            // the load as we haven't reacted at all to it and we need to reset
            if (stillWaitingForPage) {
                return Promise.reject(e);
            }
            else {
                return Promise.resolve(null);
            }
        }));
        return currentPromise;
    };
}
function resolveComponentsAndTheme(json, currentTheme) {
    currentTheme = coalesceName(currentTheme);
    return Promise.all([
        resolveComponentAndChildComponents(json, currentTheme),
        currentTheme ? resolveThemeContext(currentTheme) : Promise.resolve(null),
    ]).then(results => results[0]);
}
function pageHasAppShellData(page) {
    const pageWithAppShellData = page;
    return !!pageWithAppShellData.appShellData;
}
function isExternalRedirect(page) {
    const redirect = page;
    return redirect.componentName === 'Avensia.Scope.ExternalRedirect';
}
